/**
 * Utility functions for calculating pricing with multi-day discounts.
 * Per-day pricing: dag 1 fullpris, dag 2 -20%, dag 3–4 -30%, dag 5+ -50%.
 */

import { CartAddon, ProductOptionalComponent } from '../types';
import { isDjBookingProduct } from './bookingFlowProducts';

export interface MultiDayDiscount {
  days: number;
  discountPercentage: number;
  label: string;
}

/** Per-day discount tiers: from day index (1-based) => discount percentage (0–100). */
const PER_DAY_DISCOUNT_TIERS: { fromDay: number; discountPercentage: number }[] = [
  { fromDay: 1, discountPercentage: 0 },
  { fromDay: 2, discountPercentage: 20 },
  { fromDay: 3, discountPercentage: 30 },
  { fromDay: 5, discountPercentage: 50 }
];

/** Multiplier for a given day (1-based): 1 = full price, 2 = 0.8, 3–4 = 0.7, 5+ = 0.5 */
function getMultiplierForDay(dayIndex: number): number {
  let tier = PER_DAY_DISCOUNT_TIERS[0];
  for (const t of PER_DAY_DISCOUNT_TIERS) {
    if (dayIndex >= t.fromDay) tier = t;
  }
  return 1 - tier.discountPercentage / 100;
}

// Export for UI: label + percentage (for display in info boxes)
export const MULTI_DAY_DISCOUNTS: MultiDayDiscount[] = [
  { days: 1, discountPercentage: 0, label: '1 dag' },
  { days: 2, discountPercentage: 20, label: '2 dagar' },
  { days: 3, discountPercentage: 30, label: '3–4 dagar' },
  { days: 5, discountPercentage: 50, label: '5+ dagar' }
];

/**
 * Calculate the number of rental days between two dates
 */
const calculateRentalDays = (startDate: Date, endDate: Date): number => {
  const timeDiff = endDate.getTime() - startDate.getTime();
  const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
  return Math.max(1, daysDiff);
};

/**
 * Get a summary discount for display (highest tier that applies + effective %)
 */
function getMultiDayDiscountSummary(
  days: number,
  subtotal: number,
  discountAmount: number
): MultiDayDiscount {
  const effectivePercentage =
    subtotal > 0 ? Math.round((discountAmount / subtotal) * 100) : 0;
  const tier = MULTI_DAY_DISCOUNTS.filter(d => days >= d.days).pop() ?? MULTI_DAY_DISCOUNTS[0];
  return {
    days: tier.days,
    discountPercentage: effectivePercentage,
    label: tier.label
  };
}

/**
 * Calculate the total price for an item with per-day multi-day discount.
 * Day 1 full price, day 2 -20%, days 3–4 -30%, days 5+ -50%.
 */
export const calculateItemPrice = (
  pricePerDay: number,
  quantity: number,
  startDate: Date,
  endDate: Date,
  upgrades?: Array<{ name: string; price: number }>,
  selectedUpgrades?: string[],
  addons?: CartAddon[],
  productMeta?: { category?: string; slug?: string },
  optionalComponents?: ProductOptionalComponent[],
  removedComponents?: string[]
): {
  days: number;
  basePrice: number;
  upgradesPrice: number;
  addonsPrice: number;
  removalsDiscount: number;
  subtotal: number;
  discount: MultiDayDiscount;
  discountAmount: number;
  finalPrice: number;
} => {
  const calendarDays = calculateRentalDays(startDate, endDate);
  const pricingDays =
    productMeta && isDjBookingProduct(productMeta) ? 1 : calendarDays;
  const days = pricingDays;

  const removedSet = new Set(removedComponents || []);
  let perDayRemoval = 0;
  let fixedRemovalTotal = 0;

  for (const component of optionalComponents || []) {
    const isRemoved = removedSet.has(component.id) || removedSet.has(component.name);
    if (!isRemoved) continue;

    if (component.pricing_type === 'fixed') {
      fixedRemovalTotal += component.discount * quantity;
    } else {
      perDayRemoval += component.discount;
    }
  }

  const effectivePricePerDay = Math.max(0, pricePerDay - perDayRemoval);

  let finalBaseAtFullPrice = 0;
  let finalBaseAtReducedPrice = 0;
  for (let d = 1; d <= days; d++) {
    const mult = getMultiplierForDay(d);
    finalBaseAtFullPrice += pricePerDay * quantity * mult;
    finalBaseAtReducedPrice += effectivePricePerDay * quantity * mult;
  }

  const finalBasePrice = finalBaseAtReducedPrice - fixedRemovalTotal;
  const basePrice = pricePerDay * quantity * days;
  const removalsDiscount = Math.round(
    finalBaseAtFullPrice - finalBaseAtReducedPrice + fixedRemovalTotal
  );

  // Upgrades: per-day, same multiplier per day
  let upgradesPrice = 0;
  let finalUpgradesPrice = 0;
  if (upgrades && selectedUpgrades) {
    const dailyUpgrade = selectedUpgrades.reduce((sum, upgradeName) => {
      const upgrade = upgrades.find(u => u.name === upgradeName);
      return sum + (upgrade?.price || 0);
    }, 0) * quantity;
    upgradesPrice = dailyUpgrade * days;
    for (let d = 1; d <= days; d++) {
      finalUpgradesPrice += dailyUpgrade * getMultiplierForDay(d);
    }
  }

  // Addons: per_day use same multiplier per day; fixed addons no discount
  let addonsPrice = 0;
  let finalAddonsPrice = 0;
  if (addons && addons.length > 0) {
    for (const addon of addons) {
      const addonQuantity = addon.quantity;
      const unitPrice = addon.pricePerUnit;
      if (addon.pricingType === 'per_day') {
        const dailyAddon = unitPrice * addonQuantity;
        addonsPrice += dailyAddon * days;
        for (let d = 1; d <= days; d++) {
          finalAddonsPrice += dailyAddon * getMultiplierForDay(d);
        }
      } else {
        const fixed = unitPrice * addonQuantity;
        addonsPrice += fixed;
        finalAddonsPrice += fixed;
      }
    }
  }

  const subtotal = basePrice + upgradesPrice + addonsPrice - removalsDiscount;
  const finalPrice = Math.round(finalBasePrice + finalUpgradesPrice + finalAddonsPrice);
  const discountAmount = Math.round(subtotal - finalPrice);
  const discount = getMultiDayDiscountSummary(days, subtotal, discountAmount);

  return {
    days,
    basePrice,
    upgradesPrice,
    addonsPrice,
    removalsDiscount,
    subtotal,
    discount,
    discountAmount,
    finalPrice
  };
};

function fixedComponentRemoval(
  quantity: number,
  optionalComponents: ProductOptionalComponent[] | undefined,
  removedComponents: string[] | undefined,
): number {
  const removedSet = new Set(removedComponents || []);
  let fixedRemovalTotal = 0;
  for (const component of optionalComponents || []) {
    const isRemoved = removedSet.has(component.id) || removedSet.has(component.name);
    if (!isRemoved || component.pricing_type !== 'fixed') continue;
    fixedRemovalTotal += component.discount * quantity;
  }
  return fixedRemovalTotal;
}

/**
 * Pris för en produktrad över bokningens perioder.
 * Flerdagsrabatten börjar om i varje period. Fasta tillägg och fasta avdrag räknas en gång.
 */
export function calculateItemPriceForPeriods(
  pricePerDay: number,
  quantity: number,
  periods: Array<{ start: Date; end: Date }>,
  upgrades?: Array<{ name: string; price: number }>,
  selectedUpgrades?: string[],
  addons?: CartAddon[],
  productMeta?: { category?: string; slug?: string },
  optionalComponents?: ProductOptionalComponent[],
  removedComponents?: string[],
): { finalPrice: number; days: number } {
  if (periods.length === 0) return { finalPrice: 0, days: 0 };
  if (periods.length === 1) {
    const single = calculateItemPrice(
      pricePerDay,
      quantity,
      periods[0].start,
      periods[0].end,
      upgrades,
      selectedUpgrades,
      addons,
      productMeta,
      optionalComponents,
      removedComponents,
    );
    return { finalPrice: single.finalPrice, days: single.days };
  }

  const perDayAddons = (addons || []).filter((addon) => addon.pricingType === 'per_day');
  const fixedAddons = (addons || []).filter((addon) => addon.pricingType !== 'per_day');
  const fixedRemoval = fixedComponentRemoval(quantity, optionalComponents, removedComponents);
  let finalPrice = 0;
  let days = 0;

  periods.forEach((period) => {
    const part = calculateItemPrice(
      pricePerDay,
      quantity,
      period.start,
      period.end,
      upgrades,
      selectedUpgrades,
      perDayAddons,
      productMeta,
      optionalComponents,
      removedComponents,
    );
    finalPrice += part.finalPrice;
    days += part.days;
  });

  finalPrice += fixedRemoval * (periods.length - 1);

  if (fixedAddons.length > 0) {
    const once = calculateItemPrice(
      0,
      quantity,
      periods[0].start,
      periods[0].end,
      undefined,
      undefined,
      fixedAddons,
    );
    finalPrice += once.finalPrice;
  }

  return { finalPrice: Math.round(finalPrice), days };
}

/**
 * Calculate total cart price with multi-day discounts
 */
export const calculateCartTotal = (items: Array<{
  product: {
    pricePerDay: number;
    upgrades?: Array<{ name: string; price: number }>;
    optionalComponents?: ProductOptionalComponent[];
    category?: string;
    slug?: string;
  };
  startDate: Date;
  endDate: Date;
  quantity: number;
  options?: string[];
  removedComponents?: string[];
  addons?: CartAddon[];
}>): {
  items: Array<{
    days: number;
    basePrice: number;
    upgradesPrice: number;
    addonsPrice: number;
    removalsDiscount: number;
    subtotal: number;
    discount: MultiDayDiscount;
    discountAmount: number;
    finalPrice: number;
  }>;
  totalSubtotal: number;
  totalDiscountAmount: number;
  totalFinalPrice: number;
} => {
  const itemCalculations = items.map((item) =>
    calculateItemPrice(
      item.product.pricePerDay,
      item.quantity,
      item.startDate,
      item.endDate,
      item.product.upgrades,
      item.options,
      item.addons,
      {
        category: item.product.category,
        slug: item.product.slug,
      },
      item.product.optionalComponents,
      item.removedComponents
    )
  );

  const totalSubtotal = itemCalculations.reduce((sum, calc) => sum + calc.subtotal, 0);
  const totalDiscountAmount = itemCalculations.reduce((sum, calc) => sum + calc.discountAmount, 0);
  const totalFinalPrice = itemCalculations.reduce((sum, calc) => sum + calc.finalPrice, 0);

  return {
    items: itemCalculations,
    totalSubtotal,
    totalDiscountAmount,
    totalFinalPrice
  };
};
